// Geração de imagem, no formato do Gemini: ideias e estilos em cartões, campo de descrição com "+" (imagem de referência), modo "Imagens",
// escolha do modelo, microfone (ditado pela transcrição da rede) e Proporção. Só modelos de mercado da OpenRouter (/api/client/images*).
// Cada geração é cobrada pelo custo real; o que foi gerado fica na galeria ("Minhas imagens") e pode ser baixado, reutilizado ou apagado.
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, ChevronDown, Check, Copy, Download, Image as ImageIcon, Mic, Pencil, Plus, RefreshCcw, Square, Trash2, X } from 'lucide-react';
import { api, ApiError, brl, type GenImage, type ImageModel, type SttJob } from '../api';
import { useMeRequired } from '../me';
import { toast } from '../components/toast';
import { copyText } from '../components/CopyButton';
import { toBase64, useRecorder } from '../recorder';
import '../styles/image-gen.css';

// ---------------------------------------------------------------- ideias e estilos
type Preset = { id: string; label: string; kind: 'idea' | 'style'; prompt: string; bg: string };
const PRESETS: Preset[] = [
  { id: 'retro80', label: 'Retrospectiva dos anos 80', kind: 'idea', prompt: 'Retrato espontâneo de uma pessoa numa rua movimentada dos anos 80, jaqueta colorida, carros antigos ao fundo, fotografia analógica com grão', bg: 'linear-gradient(135deg,#2b1d14,#b8743a)' },
  { id: 'glam80', label: 'Glam dos anos 80', kind: 'idea', prompt: 'Retrato glamouroso dos anos 80, cabelo volumoso, maquiagem marcante, brincos grandes, iluminação dramática de estúdio', bg: 'linear-gradient(135deg,#2a0f24,#a23a6b)' },
  { id: 'ferias80', label: 'Férias nos anos 80', kind: 'idea', prompt: 'Casal em férias à beira-mar nos anos 80, roupas coloridas, sorrindo, foto de câmera descartável com cores quentes', bg: 'linear-gradient(135deg,#123347,#e0a14a)' },
  { id: 'disco80', label: 'Discoteca dos anos 80', kind: 'idea', prompt: 'Pista de dança de uma discoteca dos anos 80, luzes de neon, bola de espelhos, pessoas dançando', bg: 'linear-gradient(135deg,#1c1347,#d0409a)' },
  { id: 'amanhecer', label: 'Amanhecer', kind: 'idea', prompt: 'Paisagem ao amanhecer, névoa suave e luz dourada atravessando as montanhas, cinematográfico, grande angular', bg: 'linear-gradient(135deg,#16283b,#f0b25a)' },
  { id: 'ciborgue', label: 'Ciborgue', kind: 'idea', prompt: 'Ciborgue feminino num laboratório futurista, pele translúcida com circuitos vermelhos, iluminação de neon, ultra detalhado', bg: 'linear-gradient(135deg,#1b1417,#b3322f)' },
  { id: 'esboco', label: 'Esboço', kind: 'style', prompt: 'Transforme a imagem enviada em um esboço a lápis sobre papel, traços finos e sombreado leve, mantendo o assunto e a composição', bg: 'linear-gradient(135deg,#2a2a2a,#8b857a)' },
  { id: 'argila', label: 'Argila gótica', kind: 'style', prompt: 'Transforme a imagem enviada em uma cena de animação em argila (stop motion), estilo gótico e sombrio, mantendo o assunto e a composição', bg: 'linear-gradient(135deg,#1a1620,#6a5578)' },
  { id: 'surreal', label: 'Surreal', kind: 'style', prompt: 'Transforme a imagem enviada em uma pintura surrealista, deserto onírico, proporções estranhas e luz irreal, mantendo o assunto principal', bg: 'linear-gradient(135deg,#31281a,#3f8c9a)' },
  { id: 'steampunk', label: 'Steampunk', kind: 'style', prompt: 'Transforme a imagem enviada em uma cena steampunk, engrenagens de latão, vapor e cidade industrial vitoriana, mantendo o assunto e a composição', bg: 'linear-gradient(135deg,#2b2218,#a7782f)' },
  { id: 'cartoon', label: 'Desenho animado antigo', kind: 'style', prompt: 'Transforme a imagem enviada em um desenho animado em preto e branco dos anos 30, olhos grandes e traço de borracha, mantendo o assunto', bg: 'linear-gradient(135deg,#1e1e1e,#a8a8a8)' },
  { id: 'aquarela', label: 'Aquarela', kind: 'style', prompt: 'Transforme a imagem enviada em uma pintura em aquarela, pinceladas suaves e cores diluídas sobre papel texturizado, mantendo o assunto e a composição', bg: 'linear-gradient(135deg,#143240,#6fb7a6)' },
];

const ASPECTS: { id: string; label: string; w: number; h: number }[] = [
  { id: '1:1', label: 'Quadrado', w: 14, h: 14 }, { id: '16:9', label: 'Paisagem', w: 18, h: 10 }, { id: '9:16', label: 'Retrato (celular)', w: 10, h: 18 },
  { id: '4:3', label: 'Clássico', w: 16, h: 12 }, { id: '3:4', label: 'Retrato (foto)', w: 12, h: 16 },
];

const TABS = [['all', 'Tudo'], ['style', 'Mudar o estilo'], ['idea', 'Imaginação'], ['mine', 'Minhas imagens']] as const;
type Tab = typeof TABS[number][0];

const LS = { model: 'img.model', aspect: 'img.aspect' };
const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sem armazenamento */ } };

const fileUrl = (id: string) => `/api/client/images/${id}/file`;
const ext = (mime: string) => (mime === 'image/jpeg' ? 'jpg' : mime === 'image/webp' ? 'webp' : 'png');
const shortModel = (id: string) => id.replace(/^openrouter\//, '').split('/').pop() ?? id;

/** Lê a imagem escolhida, reduz para no máximo 1536 px e devolve um data URL JPEG (cabe no limite do servidor e poupa envio). */
async function prepareReference(file: File): Promise<string> {
  const src = await new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(new Error('não foi possível ler a imagem')); r.readAsDataURL(file); });
  const img = await new Promise<HTMLImageElement>((ok, no) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => no(new Error('arquivo de imagem inválido')); i.src = src; });
  const k = Math.min(1, 1536 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * k)); c.height = Math.max(1, Math.round(img.naturalHeight * k));
  const g = c.getContext('2d');
  if (!g) throw new Error('não foi possível preparar a imagem');
  g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height);   // PNG com transparência vira fundo branco no JPEG
  g.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.9);
}

/** Fecha o popover ao clicar fora ou apertar Esc. */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) close(); };
    const key = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', down); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open, close]);
  return ref;
}

/** Uma troca da conversa: o que foi pedido e o que veio (ou está vindo). */
type Turn = { id: string; prompt: string; ref: string | null; aspect: string; modelId: string; modelName: string; status: 'running' | 'done' | 'error'; item?: GenImage; error?: string; t0: number };
let session: Turn[] = [];   // a conversa sobrevive a trocar de página e voltar (a galeria guarda as imagens de qualquer jeito)
let turnSeq = 0;

/** Texto longo do pedido: mostra 5 linhas e deixa abrir o resto. */
function ClampText({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 320 || text.split('\n').length > 5;
  return (
    <>
      <p className={`ig-text${long && !open ? ' clamp' : ''}`}>{text}</p>
      {long && <button type="button" className="ig-more" onClick={() => setOpen((o) => !o)}>{open ? 'Mostrar menos' : 'Mostrar tudo'}</button>}
    </>
  );
}

/** Ações de uma imagem pronta (na conversa e no visualizador da galeria). */
function ImageActions({ it, onEdit, onAgain, onRemove, extra }: { it: GenImage; onEdit: () => void; onAgain?: () => void; onRemove: () => Promise<void> | void; extra?: React.ReactNode }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className="ig-acts">
      <a className="btn sm" href={fileUrl(it.id)} download={`imagem-${it.id.slice(0, 8)}.${ext(it.mime)}`}><Download /> Baixar</a>
      <button type="button" className="btn sm" onClick={() => { void copyText(it.prompt); toast('Descrição copiada'); }}><Copy /> Copiar descrição</button>
      <button type="button" className="btn sm" onClick={onEdit}><Pencil /> Editar esta imagem</button>
      {onAgain && <button type="button" className="btn sm" onClick={onAgain}><RefreshCcw /> Gerar de novo</button>}
      {confirm ? (
        <>
          <button type="button" className="btn sm danger" onClick={() => void onRemove()} aria-label="Confirmar: apagar imagem"><Check /> Apagar</button>
          <button type="button" className="btn sm" onClick={() => setConfirm(false)} aria-label="Cancelar"><X /></button>
        </>
      ) : <button type="button" className="btn sm ghost" onClick={() => setConfirm(true)}><Trash2 /> Apagar</button>}
      {extra}
    </div>
  );
}

/** Imagem da galeria em tamanho grande. */
function Lightbox({ it, onClose, onEdit, onRemove }: { it: GenImage; onClose: () => void; onEdit: () => void; onRemove: () => Promise<void> | void }) {
  useEffect(() => {
    const key = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);
  return createPortal(
    <div className="ig-lb" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ig-lb-card" role="dialog" aria-modal="true" aria-label="Imagem">
        <button type="button" className="ig-lb-x btn sm" onClick={onClose} aria-label="Fechar"><X /></button>
        <div className="ig-frame"><img src={fileUrl(it.id)} alt={it.prompt} /></div>
        <ClampText text={it.prompt} />
        <p className="ig-meta">{shortModel(it.model)} · {it.aspect}{it.charge != null ? ` · ${brl(it.charge)}` : ''}</p>
        <ImageActions it={it} onEdit={onEdit} onRemove={onRemove} />
      </div>
    </div>,
    document.body,
  );
}

export function ImageGen() {
  const { refresh } = useMeRequired();
  const [models, setModels] = useState<ImageModel[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [model, setModel] = useState<string>('');
  const [aspect, setAspect] = useState<string>(lsGet(LS.aspect) ?? '1:1');
  const [prompt, setPrompt] = useState('');
  const [ref, setRef] = useState<{ url: string; name: string } | null>(null);
  const [tab, setTab] = useState<Tab>('all');
  const [turns, setTurns] = useState<Turn[]>(() => session);
  const [view, setView] = useState<GenImage | null>(null);   // imagem da galeria aberta em tamanho grande
  const [now, setNow] = useState(Date.now());
  const [gallery, setGallery] = useState<GenImage[] | null>(null);
  const [galleryError, setGalleryError] = useState<string | null>(null);
  const [openModel, setOpenModel] = useState(false);
  const [openAspect, setOpenAspect] = useState(false);
  const [dictating, setDictating] = useState(false);   // áudio gravado, aguardando a transcrição
  const scrollRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => {
    alive.current = false; abort.current?.abort();
    session = session.map((t) => (t.status === 'running' ? { ...t, status: 'error', error: 'A geração foi cancelada ao sair da página.' } : t));
  }; }, []);
  useEffect(() => { session = turns; }, [turns]);

  const loadModels = useCallback(() => {
    setModelsError(null); setModels(null);
    api<ImageModel[]>('/api/client/images/models').then((l) => {
      setModels(l);
      const saved = lsGet(LS.model);
      setModel((cur) => (cur && l.some((m) => m.id === cur) ? cur : l.find((m) => m.id === saved)?.id ?? l.find((m) => /gemini.*flash/i.test(m.id))?.id ?? l[0]?.id ?? ''));
    }).catch((e) => setModelsError((e as Error).message));
  }, []);
  useEffect(() => { loadModels(); }, [loadModels]);

  const loadGallery = useCallback(() => {
    setGalleryError(null);
    api<GenImage[]>('/api/client/images').then(setGallery).catch((e) => setGalleryError((e as Error).message));
  }, []);
  useEffect(() => { if (tab === 'mine' && gallery === null && !galleryError) loadGallery(); }, [tab, gallery, galleryError, loadGallery]);

  const cur = models?.find((m) => m.id === model) ?? null;
  const running = turns.some((t) => t.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  const scrollDown = useCallback((smooth = true) => { const el = scrollRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' }); }, []);
  const last = turns[turns.length - 1];
  useEffect(() => { if (tab !== 'mine') scrollDown(); }, [turns.length, last?.status, tab, scrollDown]);   // a conversa acompanha a última mensagem

  const pickModel = (id: string) => { setModel(id); lsSet(LS.model, id); setOpenModel(false); };
  const pickAspect = (id: string) => { setAspect(id); lsSet(LS.aspect, id); setOpenAspect(false); };

  /** Com imagem de referência só vale modelo que a aceita: troca sozinho (e avisa) se o atual não aceita. */
  const ensureEditModel = (): boolean => {
    if (cur?.edits) return true;
    const alt = models?.find((m) => m.edits);
    if (!alt) { toast('Nenhum modelo liberado aceita imagem de referência', 'err'); return false; }
    pickModel(alt.id); toast(`Usando ${alt.name}, que aceita imagem de referência`);
    return true;
  };

  const setReference = async (file: File | undefined) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) { toast('Use uma imagem PNG, JPEG ou WebP', 'err'); return; }
    if (!ensureEditModel()) return;
    try { setRef({ url: await prepareReference(file), name: file.name }); taRef.current?.focus(); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  /** Reaproveita uma imagem já gerada como referência do próximo pedido. */
  const reuse = async (img: GenImage) => {
    if (!ensureEditModel()) return;
    try {
      const blob = await (await fetch(fileUrl(img.id))).blob();
      setRef({ url: await prepareReference(new File([blob], `imagem.${ext(img.mime)}`, { type: blob.type })), name: 'imagem gerada' });
      setView(null); setTab('all'); taRef.current?.focus();
    } catch (e) { toast((e as Error).message, 'err'); }
  };

  const usePreset = (p: Preset) => {
    setPrompt(p.prompt);
    if (p.kind === 'style' && !ref) { if (ensureEditModel()) fileRef.current?.click(); }   // estilo transforma uma imagem: pede a imagem
    else taRef.current?.focus();
  };

  /** Manda um pedido: ele entra na conversa na hora (carregando) e a imagem chega quando o modelo terminar. */
  const submit = async (t: Pick<Turn, 'prompt' | 'ref' | 'aspect' | 'modelId' | 'modelName'>) => {
    const id = `t${Date.now()}-${++turnSeq}`;
    const ctrl = new AbortController(); abort.current = ctrl;
    setTurns((cur) => [...cur, { ...t, id, status: 'running', t0: Date.now() }]); setNow(Date.now());
    const finish = (patch: Partial<Turn>) => setTurns((cur) => cur.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    try {
      const img = await api<GenImage>('/api/client/images', { method: 'POST', signal: ctrl.signal, body: JSON.stringify({ model: t.modelId, prompt: t.prompt, aspect: t.aspect, ...(t.ref ? { reference: t.ref } : {}) }) });
      if (!alive.current) return;
      finish({ status: 'done', item: img }); setGallery((g) => (g ? [img, ...g] : g));
      void refresh();   // o saldo mudou
    } catch (e) {
      if (!alive.current) return;
      finish({ status: 'error', error: ctrl.signal.aborted ? 'Cancelado.' : (e as Error).message || 'Não foi possível gerar a imagem.' });
    }
  };
  /** Envia o que está no campo: o campo esvazia (como no chat) e o pedido vira a mensagem da conversa. */
  const send = () => {
    const text = prompt.trim();
    if (!text || running || !cur) return;
    if (ref && !cur.edits) { toast('Este modelo não aceita imagem de referência', 'err'); return; }
    void submit({ prompt: text, ref: ref?.url ?? null, aspect, modelId: cur.id, modelName: cur.name });
    setPrompt(''); setRef(null); setTab('all');
  };
  const again = (t: Turn) => { if (!running) void submit({ prompt: t.prompt, ref: t.ref, aspect: t.aspect, modelId: t.modelId, modelName: t.modelName }); };
  const retry = (t: Turn) => { if (running) return; setTurns((cur) => cur.filter((x) => x.id !== t.id)); void submit({ prompt: t.prompt, ref: t.ref, aspect: t.aspect, modelId: t.modelId, modelName: t.modelName }); };
  const cancel = () => abort.current?.abort();
  /** Devolve o pedido ao campo para ajustar (e tira a tentativa que falhou da conversa). */
  const adjust = (t: Turn) => { setPrompt(t.prompt); if (t.ref) setRef({ url: t.ref, name: 'imagem de referência' }); setTurns((cur) => cur.filter((x) => x.id !== t.id)); taRef.current?.focus(); };
  const newIdea = () => { if (running) return; setTurns([]); setTab('all'); setPrompt(''); setRef(null); };

  // ditado: grava, manda para a transcrição da rede (a mesma do módulo Áudio) e acrescenta o texto no campo
  const transcribe = async (file: File) => {
    setDictating(true);
    try {
      const { id } = await api<{ id: string }>('/api/client/audio/stt', { method: 'POST', body: JSON.stringify({ audioBase64: await toBase64(file), filename: file.name, language: 'pt' }) });
      for (let i = 0; i < 80 && alive.current; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const j = await api<SttJob>(`/api/client/audio/stt/${id}`);
        if (j.status === 'failed') throw new Error(j.error ?? 'a transcrição falhou');
        if (j.status === 'done') { const t = (j.text ?? '').trim(); if (t) setPrompt((p) => (p.trim() ? `${p.trimEnd()} ${t}` : t)); else toast('Não entendi o áudio', 'err'); taRef.current?.focus(); return; }
      }
      if (alive.current) toast('A transcrição demorou demais', 'err');
    } catch (e) { if (alive.current) toast((e as Error).message, 'err'); }
    finally { if (alive.current) setDictating(false); }
  };
  const rec = useRecorder((f) => { void transcribe(f); });

  const remove = async (img: GenImage) => {
    try { await api(`/api/client/images/${img.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); return; } }
    setGallery((g) => (g ?? []).filter((x) => x.id !== img.id));
    setTurns((cur) => cur.filter((x) => x.item?.id !== img.id)); setView(null);   // some também da conversa
    toast('Imagem apagada');
  };

  const keys = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
  };
  useEffect(() => {   // o campo cresce com o texto
    const t = taRef.current; if (!t) return;
    t.style.height = 'auto'; t.style.height = `${Math.min(t.scrollHeight, 160)}px`;
  }, [prompt]);

  const modelRef = useDismiss(openModel, useCallback(() => setOpenModel(false), []));
  const aspectRef = useDismiss(openAspect, useCallback(() => setOpenAspect(false), []));
  const asp = ASPECTS.find((a) => a.id === aspect) ?? ASPECTS[0];
  const inFeed = turns.length > 0 && tab !== 'mine';
  const noModels = models !== null && models.length === 0;
  const tiles = PRESETS.filter((p) => tab === 'all' || p.kind === tab);

  const turnView = (t: Turn) => {
    const [w, h] = t.aspect.split(':').map(Number);
    const secs = Math.max(0, Math.floor((now - t.t0) / 1000));
    return (
      <div key={t.id} className="ig-turn">
        <div className="ig-user">
          <div className="ig-bubble">
            {t.ref && <img className="ig-bubble-ref" src={t.ref} alt="Imagem de referência" />}
            <ClampText text={t.prompt} />
          </div>
          <small>{t.modelName} · {t.aspect}</small>
        </div>
        <div className="ig-ai">
          {t.status === 'running' && (
            <>
              <div className="ig-frame ig-wait" style={{ aspectRatio: `${w} / ${h}` }} role="status" aria-live="polite">
                <span>Gerando imagem… {secs}s{secs >= 20 ? <em>modelos de imagem costumam levar de 10 a 60 segundos</em> : null}</span>
              </div>
              <div className="ig-acts"><button type="button" className="btn sm" onClick={cancel}><Square /> Cancelar</button></div>
            </>
          )}
          {t.status === 'error' && (
            <>
              <div className="ig-err" role="alert"><b>Não foi possível gerar a imagem</b><p>{t.error}</p></div>
              <div className="ig-acts">
                <button type="button" className="btn sm" onClick={() => retry(t)} disabled={running}><RefreshCcw /> Tentar de novo</button>
                <button type="button" className="btn sm ghost" onClick={() => adjust(t)}><Pencil /> Ajustar descrição</button>
              </div>
            </>
          )}
          {t.status === 'done' && t.item && (
            <>
              <div className="ig-frame"><img src={fileUrl(t.item.id)} alt={t.item.prompt} onLoad={() => scrollDown(false)} /></div>
              <p className="ig-meta">{shortModel(t.item.model)} · {t.item.aspect}{t.item.charge != null ? ` · ${brl(t.item.charge)}` : ''}</p>
              <ImageActions it={t.item} onEdit={() => void reuse(t.item!)} onAgain={running ? undefined : () => again(t)} onRemove={() => remove(t.item!)} />
            </>
          )}
        </div>
      </div>
    );
  };

  let stage;
  if (modelsError) stage = <div className="empty"><b>Não foi possível carregar os modelos</b><p>{modelsError}</p><button type="button" className="btn" onClick={loadModels}>Tentar de novo</button></div>;
  else if (noModels) stage = <div className="empty"><b>Nenhum modelo de imagem liberado</b><p>A geração de imagem usa modelos de mercado via OpenRouter. O gestor precisa informar a chave da OpenRouter e liberar os modelos.</p></div>;
  else if (tab === 'mine') {
    stage = galleryError ? <div className="empty"><b>Não foi possível carregar</b><p>{galleryError}</p><button type="button" className="btn" onClick={loadGallery}>Tentar de novo</button></div>
      : gallery === null ? <div className="ig-grid" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <div key={i} className="skel ig-skel" />)}</div>
        : gallery.length === 0 ? <div className="empty"><b>Nenhuma imagem ainda</b><p>As imagens que você gerar ficam guardadas aqui.</p></div>
          : <div className="ig-grid">{gallery.map((g) => (
            <button key={g.id} type="button" className="ig-thumb" title={g.prompt} onClick={() => setView(g)}><img src={fileUrl(g.id)} alt={g.prompt} loading="lazy" /></button>
          ))}</div>;
  } else if (inFeed) stage = <div className="ig-feed">{turns.map(turnView)}</div>;
  else {
    stage = (
      <div className="ig-tiles">
        {tiles.map((p) => (
          <button key={p.id} type="button" className="ig-tile" style={{ background: p.bg }} onClick={() => usePreset(p)}>
            <span>{p.label}</span>{p.kind === 'style' && <em>usa uma imagem sua</em>}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="ig-page">
      <h1 className="sr-only">Geração de imagem</h1>
      {inFeed ? (
        <div className="ig-top">
          <button type="button" className="ig-chip" onClick={newIdea} disabled={running} title={running ? 'Aguarde a imagem terminar' : undefined}><Plus /> Nova ideia</button>
          <button type="button" className="ig-chip" onClick={() => setTab('mine')}>Minhas imagens</button>
        </div>
      ) : (
        <div className="ig-tabs" role="tablist" aria-label="Ideias">
          {TABS.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>
      )}

      <div className="ig-scroll" ref={scrollRef}><div className="ig-inner">{tab === 'mine' && turns.length > 0 && (
        <button type="button" className="ig-chip ig-back" onClick={() => setTab('all')}>← Voltar à conversa</button>
      )}{stage}</div></div>

      <div className="ig-dock">
        <div className="ig-composer">
          {ref && (
            <div className="ig-ref"><img src={ref.url} alt="Imagem de referência" /><span>{ref.name}</span>
              <button type="button" onClick={() => setRef(null)} aria-label="Remover imagem de referência"><X /></button></div>
          )}
          <textarea ref={taRef} rows={1} value={prompt} maxLength={4000} placeholder={ref ? 'Diga o que mudar nesta imagem' : 'Descreva sua imagem'} aria-label="Descrição da imagem"
            disabled={noModels || models === null} onChange={(e) => setPrompt(e.target.value)} onKeyDown={keys} />
          <div className="ig-bar">
            <button type="button" className="ig-icon" onClick={() => fileRef.current?.click()} disabled={noModels || models === null} aria-label="Adicionar imagem de referência" title="Adicionar imagem de referência"><Plus /></button>
            <span className="ig-mode"><ImageIcon /> Imagens</span>
            <span className="ig-sp" />
            <div className="ig-pop-wrap" ref={modelRef}>
              <button type="button" className="ig-model" aria-haspopup="listbox" aria-expanded={openModel} onClick={() => setOpenModel((o) => !o)} disabled={!models?.length}>
                <span>{cur ? cur.name : models === null ? 'carregando…' : 'sem modelos'}</span><ChevronDown />
              </button>
              {openModel && models && (
                <div className="ig-pop ig-models" role="listbox" aria-label="Modelo de imagem">
                  {models.map((m) => {
                    const off = Boolean(ref) && !m.edits;
                    return (
                      <button key={m.id} type="button" role="option" aria-selected={m.id === model} disabled={off} className={m.id === model ? 'on' : ''}
                        title={off ? 'Não aceita imagem de referência' : undefined} onClick={() => pickModel(m.id)}>
                        <span className="ig-m-name">{m.name}<small>{m.provider}{m.edits ? ' · aceita imagem' : ''}</small></span>
                        <span className="ig-m-price">{m.estimateBrl != null ? `≈ ${brl(m.estimateBrl, 2)}` : ''}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {rec.supported && (rec.recording
              ? <button type="button" className="ig-icon rec" onClick={rec.stop} aria-label="Parar e transcrever"><Square /><span>{Math.floor(rec.seconds)}s</span></button>
              : <button type="button" className="ig-icon" onClick={() => void rec.start()} disabled={dictating || noModels} aria-label="Ditar a descrição" title="Ditar a descrição">{dictating ? <span className="ig-spin" aria-label="Transcrevendo" /> : <Mic />}</button>)}
            <button type="button" className="ig-send" onClick={send} disabled={!prompt.trim() || running || !cur} aria-label="Gerar imagem" title={running ? 'Aguarde a imagem terminar' : 'Gerar imagem (Enter)'}><ArrowUp /></button>
          </div>
        </div>

        <div className="ig-aspect ig-pop-wrap" ref={aspectRef}>
          <button type="button" className="ig-aspect-btn" aria-haspopup="listbox" aria-expanded={openAspect} onClick={() => setOpenAspect((o) => !o)}>
            <i style={{ width: asp.w, height: asp.h }} /> Proporção · {asp.id} <ChevronDown />
          </button>
          {openAspect && (
            <div className="ig-pop ig-aspects" role="listbox" aria-label="Proporção">
              {ASPECTS.map((a) => (
                <button key={a.id} type="button" role="option" aria-selected={a.id === aspect} className={a.id === aspect ? 'on' : ''} onClick={() => pickAspect(a.id)}>
                  <i style={{ width: a.w, height: a.h }} /><span>{a.label}</span><small>{a.id}</small>
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="ig-note">
          {rec.error ? rec.error : running ? 'Gerando… você já pode escrever a próxima descrição.' : <>A descrição{ref ? ' e a imagem' : ''} vão para um provedor externo (OpenRouter); evite dados sensíveis. {cur?.estimateBrl != null && <>{cur.estimateKind === 'history' ? `Suas imagens com este modelo custaram em média ≈ ${brl(cur.estimateBrl, 2)}` : `Estimativa ≈ ${brl(cur.estimateBrl, 2)} por imagem`}; a cobrança é o custo real do modelo.</>}</>}
        </p>
      </div>

      {view && <Lightbox it={view} onClose={() => setView(null)} onEdit={() => void reuse(view)} onRemove={() => remove(view)} />}
      <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp" aria-label="Imagem de referência" onChange={(e) => { void setReference(e.target.files?.[0]); e.target.value = ''; }} />
    </div>
  );
}
